/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { MouseEvent } from "react";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { localPoint } from "@visx/event";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { When } from "react-if";
import { theme } from "@nordcloud/gnui";
import {
  ChartType,
  Granularity,
  Period,
  TopGroupsCostChartProps,
} from "~/components/Charts/CostAnalysis/types";
import {
  getInnerBarPadding,
  getOuterBarPadding,
} from "~/components/Charts/utils";
import { EstatePeriod } from "~/components/DateRangeSelector";
import { useCurrency } from "~/hooks";
import { formatMoneyLabel, noop } from "~/tools";
import { useStackBarChart } from "../hooks";
import { BarStackRenderer } from "./BarStackRenderer";
import { StackBarTooltip } from "./StackBarTooltip";
import { TooltipProps } from "./types";
import { formatTickDay } from "./utils";

type Props = {
  width: number;
  height: number;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  data: TopGroupsCostChartProps[];
  period: EstatePeriod | Period;
  colors: { [k: string]: string };
  keys: string[];
  showTooltip: (args: TooltipProps) => void;
  hideTooltip: () => void;
  containerRef: (element: HTMLElement | SVGElement | null) => void;
  isEstateView: boolean;
  showOthers?: boolean;
  chartType?: ChartType;
};

export function StackBarChartData({
  startDate,
  endDate,
  granularity,
  width,
  height,
  data,
  period,
  chartType,
  colors,
  keys,
  showOthers = false,
  showTooltip,
  hideTooltip,
  containerRef,
  isEstateView,
}: Props) {
  const { currency } = useCurrency();

  const {
    xDates,
    getDate,
    hasNegativeValues,
    dateRangeDates,
    chartData,
    tickDateFormat,
    ticks,
    xScale,
    yScale,
    verticalMargin,
    xMax,
    yMax,
  } = useStackBarChart({
    startDate,
    endDate,
    granularity,
    data,
    showOthers,
    chartType,
    period,
    keys,
    width,
    height,
  });

  const showTooltipElement = (event: MouseEvent, index: number) => {
    const coords = localPoint(event, event);

    showTooltip({
      tooltipLeft: coords?.x ?? event.clientX,
      tooltipTop: (coords?.y ?? event.clientY) - window.scrollY,
      tooltipData: (
        <StackBarTooltip
          data={chartData}
          index={index}
          granularity={granularity}
          currency={currency}
          colors={colors}
          dateRangeDates={dateRangeDates}
          chartType={chartType}
          areKeysByGranularity={!isEstateView}
        />
      ),
    });
  };

  xScale.paddingInner(getInnerBarPadding(xDates.length, width));
  xScale.paddingOuter(getOuterBarPadding(xDates.length));

  return (
    <svg
      ref={containerRef}
      width="100%"
      height={height}
      css={{ marginTop: theme.spacing.spacing04 }}
      onMouseEnter={hideTooltip}
    >
      <Group top={verticalMargin / 2} left={50}>
        <GridRows
          scale={yScale}
          width={xMax}
          strokeDasharray="1,3"
          stroke={theme.color.border.focus}
          strokeOpacity={1}
          pointerEvents="none"
          numTicks={5}
        />
        <When condition={hasNegativeValues}>
          <GridRows
            scale={yScale}
            width={xMax}
            stroke={theme.color.border.border02}
            strokeOpacity={1}
            pointerEvents="none"
            numTicks={1}
            tickValues={[0]}
          />
        </When>
        <AxisLeft
          top={0}
          scale={yScale}
          stroke={theme.color.border.focus}
          tickStroke="transparent"
          numTicks={6}
          tickFormat={(value) => formatMoneyLabel(value, currency)}
          left={2}
          tickLabelProps={() => ({
            fill: theme.color.text.text02,
            fontSize: 10,
            textAnchor: "end",
          })}
        />
        <AxisBottom
          top={yMax}
          scale={xScale}
          stroke={theme.color.border.focus}
          tickStroke={theme.color.border.focus}
          numTicks={ticks}
          tickFormat={(index) => formatTickDay(xDates[index], tickDateFormat)}
          tickLabelProps={() => ({
            fill: theme.color.text.text02,
            fontSize: 10,
            textAnchor: "middle",
          })}
        />
        <BarStackRenderer
          keys={keys}
          data={chartData ?? []}
          yScale={yScale}
          xScale={xScale}
          x={getDate}
          chartType={chartType}
          colors={colors}
          onMouseLeave={noop}
          onMouseOver={(event, index) => {
            showTooltipElement(event, index);
          }}
        />
      </Group>
    </svg>
  );
}
